<template>
   <div>
      <navbar-component />
      <main class="bg-blueGray-100">
         <section class="relative overflow-hidden pt-16 py-20 md:py-28" :style="{ backgroundColor: '#111827' }">
            <div class="absolute inset-0 bg-center bg-cover" :style="{ backgroundImage: `url(${headerImage})`, opacity: 0.4 }"></div>
            <div class="absolute inset-0" :style="{ backgroundColor: 'rgba(0, 0, 0, 0.68)' }"></div>
            <div class="container relative mx-auto px-4 md:px-6">
               <div class="max-w-3xl">
                  <span class="inline-block bg-blue-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-5 shadow-sm">
                     Edge Product
                  </span>
                  <h1 class="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-5">pumpLink7 powered by WaveEdge</h1>
                  <p class="text-blueGray-100 text-lg md:text-xl leading-relaxed max-w-2xl">
                     Rugged hardware with a modern operator experience, secure remote access, and the same consistent design language as the rest of the AMI product ecosystem.
                  </p>
               </div>
            </div>
         </section>

         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-4xl font-bold text-white mb-3">Deployment versatility</h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     Deploy pumpLink7 at the asset or in remote control centers with the same secure data path and familiar operational workflow.
                  </p>
               </div>
               <div class="grid lg:grid-cols-2 gap-5 md:gap-6">
                  <div v-for="deployment in deployments" :key="deployment.title" class="bg-blueGray-900 p-6 md:p-7 rounded-xl border border-blueGray-700 shadow-sm">
                     <div class="flex items-start">
                        <div class="w-12 h-12 rounded-full flex items-center justify-center mr-4 border-2" :class="deployment.iconWrapClass">
                           <i :class="deployment.icon"></i>
                        </div>
                        <div>
                           <p class="text-xs font-bold uppercase tracking-wide mb-1" :class="deployment.labelClass">{{ deployment.label }}</p>
                           <h4 class="text-xl font-bold text-white">{{ deployment.title }}</h4>
                           <p class="text-blueGray-300 mt-2">{{ deployment.text }}</p>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-4xl font-bold text-white mb-3">Industrial-grade innovation</h2>
                  <p class="text-lg text-blueGray-300">Core capabilities for mission-critical monitoring and control.</p>
               </div>
               <div class="grid md:grid-cols-2 gap-5 md:gap-6 mb-12">
                  <div v-for="feature in features" :key="feature.title" class="bg-blueGray-800 rounded-xl p-5 md:p-6 shadow-sm border border-blueGray-700">
                     <div class="flex items-center gap-3 mb-3">
                        <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2" :class="feature.iconWrapClass">
                           <i :class="feature.icon"></i>
                        </div>
                        <h3 class="text-xl md:text-2xl font-bold text-white leading-tight">{{ feature.title }}</h3>
                     </div>
                     <p class="text-blueGray-300 text-sm md:text-base leading-relaxed">{{ feature.text }}</p>
                  </div>
               </div>
               
               <div class="bg-blueGray-800 rounded-2xl p-8 md:p-10 border border-blueGray-700 text-center max-w-3xl mx-auto">
                  <h3 class="text-2xl font-bold text-white mb-4">Powered by WaveEdge</h3>
                  <p class="text-blueGray-300 mb-6">
                     pumpLink7 is built on the WaveEdge industrial runtime, providing a robust, secure, and flexible foundation for all your control and monitoring needs.
                  </p>
                  <router-link to="/waveedge" class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200">
                     Learn more about WaveEdge <i class="fas fa-arrow-right ml-2"></i>
                  </router-link>
               </div>
            </div>
         </section>

      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import headerImage from "@/assets/img/industrial-engineer-programming-machine-control.jpg";

export default {
   name: "PumpLink7",
   components: {
      NavbarComponent,
      FooterComponent,
   },
   data() {
      return {
         headerImage,
         // Deployment blocks for local and remote operation.
         deployments: [
            {
               label: "Local Interface",
               title: "Integrated HMI Systems",
               icon: "fas fa-desktop",
               text: "Direct machine interaction via industrial touchscreens with low-latency visual feedback.",
               labelClass: "text-cyan-300",
               iconWrapClass: "bg-cyan-900 text-cyan-200 border-cyan-400",
            },
            {
               label: "Remote Ready",
               title: "Headless WebHMIs",
               icon: "fas fa-cloud",
               text: "Secure browser-based access from any device for distributed infrastructure and control centers.",
               labelClass: "text-lightBlue-300",
               iconWrapClass: "bg-blue-900 text-lightBlue-200 border-lightBlue-400",
            },
         ],
         // Core product capabilities.
         features: [
            {
               title: "Fully Compatible",
               icon: "fas fa-sync-alt",
               text: "Still fully compatible with AMI's DVCommand and MPC.",
               iconWrapClass: "bg-emerald-900 text-emerald-300 border-emerald-500",
            },
            {
               title: "Robust Controls",
               icon: "fas fa-sliders-h",
               text: "Has same robust controls for engine and transmission control, logging, remote operation and viewing.",
               iconWrapClass: "bg-blue-900 text-lightBlue-300 border-lightBlue-400",
            },
            {
               title: "Enhanced Flexibility",
               icon: "fas fa-project-diagram",
               text: "Is now more flexible that we can develop more IO on it, more protocols, headless mode, secure command and control structure, cloud integration natively.",
               iconWrapClass: "bg-amber-900 text-amber-300 border-amber-400",
            },
            {
               title: "Advanced CANBUS",
               icon: "fas fa-network-wired",
               text: "Also with more CANBUS features for better filtering of bus data, full canTRACE logging and CANBUS playback to remotely simulate or troubleshoot equipment.",
               iconWrapClass: "bg-orange-900 text-orange-300 border-orange-400",
            },
         ],
      };
   },
   mounted() {
      document.title = "pumpLink7 Desktop";
   },
};
</script>

<style>
.glass-effect {
   backdrop-filter: blur(8px);
   background: rgb(255 255 255 / 88%);
}
</style>
